import { InlandMarineTypes, FarmRiskType, lookupsJson, FarmScheduleCoverageType } from '@ourbranch/lookups';
import { createColorPalette } from '@ourbranch/ui-core';
const { deductibleAllOther: allOtherOptions, scheduledFarmPropertyCategories } = lookupsJson.farm;
const dwellingInlandMarineItemTypes = [
    InlandMarineTypes.Atv,
    InlandMarineTypes.BoatDock,
    InlandMarineTypes.Boats,
    InlandMarineTypes.CoinCollection,
    InlandMarineTypes.FineArts,
    InlandMarineTypes.Furs,
    InlandMarineTypes.GolfCarts,
    InlandMarineTypes.GolfEquipment,
    InlandMarineTypes.Guns,
    InlandMarineTypes.Jewelry,
    InlandMarineTypes.LawnMowers,
    InlandMarineTypes.MiscellaneousProperty,
    InlandMarineTypes.MusicalInstruments,
    InlandMarineTypes.PersonalTools,
    InlandMarineTypes.Silverware,
    InlandMarineTypes.SportingEquipment,
    InlandMarineTypes.Tack,
    InlandMarineTypes.UnmannedAircraft
].sort((a, b) => a.id.localeCompare(b.id));
const brand = {
    constants: {
        SHORT_NAME: 'Forreston',
        FULL_NAME: 'Forreston Mutual Insurance',
        LEGAL_NAME: 'Forreston Mutual Insurance Company',
        EMAIL_SENDER: 'Forreston Mutual',
        EMAIL_BCC: 'info@forrestonmutual.com',
        SUPPORT_NAME: 'Forreston Mutual',
        DOMAIN: 'forrestonmutual.com',
        SUPPORT_EMAIL: 'support@forrestonmutual.com',
        DISPLAY_NAME: 'Forreston',
        MARKETING_NAME: 'Forreston Mutual Insurance',
        POLICY_ISSUER: 'Forreston Mutual Insurance Company',
        CLAIMS_HANDLER: 'Forreston',
        REFERRAL_NAME: 'Forreston Mutual',
        SOCIAL_HANDLE: 'Forreston',
        PAYMENT_ADDRESS: 'Forreston Mutual PO Box 686 Forreston, IL 61030',
        EXPEDITED_PAYMENT_ADDRESS: 'Forreston Mutual Insurance 208 S Walnut Forreston, IL 61030',
        CUSTOMER_SERVICE_PHONE: '800-938-2270',
        CUSTOMER_SERVICE_PHONE_LETTERS: '800-938-2270',
        CLAIMS_PHONE: '(123)-456-7890',
        CLAIMS_EMAIL: 'this-is@a.placeholder',
        PRIVACY_POLICY_URL: 'https://www.forrestonmutual.com/privacy-policy/',
        // TODO: Get terms of use URL for Forreston
        TERMS_OF_USE_URL: '#'
    },
    features: {
        account: {
            policy: {
                showBillingTab: true,
                showDocumentsTab: true,
                showCoveragesTab: false,
                showPolicyInformationTab: false,
                showPriceBreakdownTab: false,
                showDiscountsTab: false
            },
            service: {
                claimsEnabled: false,
                frontChatEnabled: false,
                helpCenterEnabled: false,
                taskCenterEnabled: false
            },
            marketing: {
                referralsEnabled: false,
                showUpsellPage: false
            }
        }
    },
    theme: {
        palette: createColorPalette({
            primaryMain: '#A6192E',
            backgroundsMain: '#F0F0F0',
            typographyMain: '#3A3A3A',
            controlsMain: '#D4D0CD',
            errorMain: '#7A0027'
        }),
        loaderColor: '#BB6058',
        showLogoInLoader: false,
        showAccountBackgroundHero: false,
        logoWidthDesktop: 320,
        logoWidthMobile: 230
    },
    config: {
        supportedStates: ['IL'],
        allowedPolicyTypes: ['F'], // Farm only
        features: {
            discounts: {
                hasFutureEffectiveDateDiscount: false,
                hasAdvanceQuoteDiscount: false,
                hasPaperlessDiscount: false
            },
            shouldAutoExcludeCosmeticDamage: true
        },
        quote: {
            phoneRequired: true,
            applicantDateOfBirthAtNewOffer: true
        },
        hiddenFields: [
            'applicant.insuranceScore',
            'applicant.gender',
            'applicant.maritalStatus',
            'applicant.highestEducation'
        ],
        farm: {
            IL: {
                // Mine Subsidence coverage limit for all farm structures (GitHub issue #28634)
                mineSubsidenceCoverageLimit: 750000,
                // Minimum Coverage A floor for dwelling risks (GitHub issue #28995)
                minimumDwellingCoverageA: 1000,
                // Forreston does not offer unscheduled personal property on any risk type (GitHub issue #28933)
                hideUnscheduledPersonalProperty: true,
                [FarmRiskType.Fo1]: {
                    hiddenCoverages: ['earthquakeCoverage'],
                    customFormComponentPaths: {
                        cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
                        deductibleAllOther: 'base/deductible-all-other',
                        deductibleWindHail: 'base/deductible-wind-hail',
                        roofLossSettlement: 'base/roof-loss-settlement',
                        repairRebuildRequirement: 'base/repair-rebuild-requirement',
                        coverageMS: 'base/coverage-ms',
                        coverageB: 'base/coverage-b',
                        lossSettlement: 'base/loss-settlement',
                        replCostMobileHome: 'base/repl-cost-mobile-home'
                    },
                    inlandMarine: {
                        itemTypes: dwellingInlandMarineItemTypes,
                        inlandMarineDeductible: allOtherOptions.filter((option) => [250, 500, 1000, 2500, 5000].includes(option.id))
                    },
                    /**
                     * Business rules for Forreston FO1:
                     *
                     * Coverage A:
                     * - FO1 always uses ACV loss settlement
                     * - Home/Modular Home (FD)/Barndominium: 50-100% of ACV
                     * - Mobile Home: $7/sq ft minimum (based on sqFt), max 100% ACV
                     *
                     * Coverage C (Personal Property):
                     * - Primary Residence: $0 - Coverage A limit (max), ACV
                     * - Seasonal: $0 - Coverage A limit (max), ACV
                     * - Tenant Occupied Rental: $0 - $25,000 (max), ACV
                     * - Vacant: $0 - Coverage A limit (max), ACV
                     *
                     * Coverage D (Additional Living Cost):
                     * - Primary Residence: $0 - $3,000
                     * - Seasonal: $0 - $3,000
                     * - Tenant Occupied Rental: $0 - $3,000
                     * - Vacant: $0 only (not editable)
                     *
                     * Related issues: #28616, #28614
                     */
                    businessRules: {
                        coverageA: {
                            // For Home, Modular Home, Barndominium: 50-100% ACV
                            acvBasedStructures: {
                                propertyTypes: ['H', 'FD', 'B'], // Home, Modular Home (FD), Barndominium
                                minPercent: 0.5, // 50% ACV minimum
                                maxPercent: 1.0 // 100% ACV maximum
                            },
                            // For Mobile Home: $7/sqft minimum, max ACV
                            mobileHome: {
                                propertyTypes: ['MH'],
                                minPerSqFt: 7,
                                maxPercent: 1.0 // 100% ACV maximum
                            }
                        },
                        coverageC: {
                            byResidenceUsage: {
                                P: { min: 0, max: 'coverageA' }, // Primary Residence
                                S: { min: 0, max: 'coverageA' }, // Seasonal
                                R: { min: 0, max: 25000 }, // Tenant Occupied Rental
                                V: { min: 0, max: 'coverageA' } // Vacant
                            }
                        },
                        coverageD: {
                            byResidenceUsage: {
                                P: { min: 0, max: 3000, allowEditing: true }, // Primary Residence
                                S: { min: 0, max: 3000, allowEditing: true }, // Seasonal
                                R: { min: 0, max: 3000, allowEditing: true }, // Tenant Occupied Rental
                                V: { min: 0, max: 0, allowEditing: false } // Vacant - must be $0, not editable
                            }
                        }
                    }
                },
                [FarmRiskType.Fo2]: {
                    hiddenCoverages: ['earthquakeCoverage'],
                    permissionRestrictedOptions: {
                        roofLossSettlement: {
                            permission: 'canApproveUWReview',
                            hiddenOptionsWithoutPermission: ['RC']
                        }
                    },
                    customFormComponentPaths: {
                        cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
                        deductibleAllOther: 'base/deductible-all-other',
                        deductibleWindHail: 'base/deductible-wind-hail',
                        coverageB: 'base/coverage-b',
                        repairRebuildRequirement: 'base/repair-rebuild-requirement',
                        coverageMS: 'base/coverage-ms',
                        roofLossSettlement: 'base/roof-loss-settlement',
                        coverageD: 'FO2/coverage-d',
                        replCostMobileHome: 'base/repl-cost-mobile-home'
                    },
                    inlandMarine: {
                        itemTypes: dwellingInlandMarineItemTypes,
                        inlandMarineDeductible: allOtherOptions.filter((option) => [250, 500, 1000, 2500, 5000].includes(option.id))
                    },
                    /**
                     * Business rules for Forreston FO2:
                     *
                     * Coverage A:
                     * - When ACV loss settlement is selected: Same rules as FO1
                     * - When RC loss settlement is selected: Keep existing 80-100% RCE rules
                     * - Home/Modular Home (FD)/Barndominium (ACV): 50-100% of ACV
                     * - Mobile Home (ACV): $7/sq ft minimum (based on sqFt), max 100% ACV
                     *
                     * Coverage C (Personal Property) - varies by loss settlement:
                     * ACV:
                     *   - Primary: $0 to Coverage A (default 50% of Coverage A)
                     *   - Seasonal: $0 to Coverage A (default 50% of Coverage A)
                     *   - Tenant: $5,000 to $25,000
                     *   - Vacant: $0 only
                     * RC:
                     *   - Primary: 50% Coverage A to Coverage A
                     *   - Seasonal: 50% Coverage A to Coverage A
                     *   - Tenant: $5,000 to $25,000
                     *   - Vacant: $0 only
                     *
                     * Coverage D business rules for Forreston FO2 (GitHub issue #29013):
                     * - ACV: All residence types default $3,000, can decrease to $0, cannot increase. Vacant = $0 locked.
                     * - RC + Primary/Seasonal: 20% of Coverage A, locked (not editable)
                     * - RC + Tenant: $3,000 default, can increase up to $50,000
                     * - RC + Vacant: $0 locked
                     *
                     * Related issues: #28616, #29011, #29013
                     */
                    businessRules: {
                        coverageA: {
                            acvBasedStructures: {
                                propertyTypes: ['H', 'FD', 'B'],
                                minPercent: 0.5,
                                maxPercent: 1.0
                            },
                            mobileHome: {
                                propertyTypes: ['MH'],
                                minPerSqFt: 7,
                                maxPercent: 1.0
                            }
                        },
                        coverageC: {
                            byLossSettlement: {
                                ACV: {
                                    byResidenceUsage: {
                                        P: { min: 0, max: 'coverageA' }, // Primary: $0 to Coverage A
                                        S: { min: 0, max: 'coverageA' }, // Seasonal: $0 to Coverage A
                                        R: { min: 5000, max: 25000 }, // Tenant: $5K to $25K
                                        V: { min: 0, max: 0 } // Vacant: $0 only
                                    }
                                },
                                RC: {
                                    byResidenceUsage: {
                                        P: { minPctOfCoverageA: 0.5, max: 'coverageA' }, // Primary: 50% CovA to CovA
                                        S: { minPctOfCoverageA: 0.5, max: 'coverageA' }, // Seasonal: 50% CovA to CovA
                                        R: { min: 5000, max: 25000 }, // Tenant: $5K to $25K
                                        V: { min: 0, max: 0 } // Vacant: $0 only
                                    }
                                }
                            }
                        },
                        coverageD: {
                            byLossSettlement: {
                                ACV: {
                                    byResidenceUsage: {
                                        P: { min: 0, max: 3000, allowEditing: true },
                                        S: { min: 0, max: 3000, allowEditing: true },
                                        R: { min: 0, max: 3000, allowEditing: true },
                                        V: { min: 0, max: 0, allowEditing: false }
                                    }
                                },
                                RC: {
                                    byResidenceUsage: {
                                        P: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                                        S: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                                        R: { min: 3000, max: 50000, allowEditing: true },
                                        V: { min: 0, max: 0, allowEditing: false }
                                    }
                                }
                            }
                        }
                    }
                },
                [FarmRiskType.Fo3]: {
                    hiddenCoverages: [
                        'earthquakeCoverage',
                        'expandedDebrisRemoval',
                        'businessProperty',
                        'sprinklerLeakage',
                        'inlandFlood',
                        'windHailExclusion'
                    ],
                    permissionRestrictedOptions: {
                        roofLossSettlement: {
                            permission: 'canApproveUWReview',
                            hiddenOptionsWithoutPermission: ['RC']
                        }
                    },
                    constructionTypeExemptPropertyTypes: ['MH'],
                    customFormComponentPaths: {
                        deductibleAllOther: 'base/deductible-all-other',
                        deductibleWindHail: 'base/deductible-wind-hail',
                        roofLossSettlement: 'base/roof-loss-settlement',
                        repairRebuildRequirement: 'base/repair-rebuild-requirement',
                        coverageB: 'base/coverage-b',
                        coverageMS: 'base/coverage-ms',
                        coverageD: 'FO3/coverage-d',
                        cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
                        repairRebuildRequirementCalled: 'FO3/repair-rebuild-requirement',
                        personalPropertyReplCost: 'FO3/personal-property-repl-cost',
                        lossSettlement: 'base/loss-settlement',
                        replCostMobileHome: 'base/repl-cost-mobile-home'
                    },
                    inlandMarine: {
                        itemTypes: dwellingInlandMarineItemTypes,
                        inlandMarineDeductible: allOtherOptions.filter((option) => [250, 500, 1000, 2500, 5000].includes(option.id))
                    },
                    /**
                     * Business rules for Forreston FO3:
                     *
                     * Coverage A:
                     * - FO-3 dwelling risks: Coverage A must be 90-100% of RCE (min $1k)
                     *
                     * Coverage C (Personal Property) - FO3 is always RC:
                     * - Primary: 75% Coverage A to Coverage A
                     * - Seasonal: 75% Coverage A to Coverage A
                     * - Tenant: $5,000 to $25,000
                     * - Vacant: $0 only
                     *
                     * Coverage D business rules for Forreston FO3 (GitHub issue #29013):
                     * - FO3 is always RC loss settlement
                     * - Primary/Seasonal: 20% of Coverage A, locked (not editable)
                     * - Tenant: $3,000 default, can increase up to $50,000
                     * - Vacant: $0 locked
                     *
                     * Related issues: #28684, #29011, #29013
                     */
                    businessRules: {
                        coverageA: {
                            rceBasedStructures: {
                                minPercent: 0.9, // 90% of RCE minimum
                                maxPercent: 1.0 // 100% of RCE maximum
                            }
                        },
                        coverageC: {
                            byResidenceUsage: {
                                P: { minPctOfCoverageA: 0.75, max: 'coverageA' }, // Primary: 75% CovA to CovA
                                S: { minPctOfCoverageA: 0.75, max: 'coverageA' }, // Seasonal: 75% CovA to CovA
                                R: { min: 5000, max: 25000 }, // Tenant: $5K to $25K
                                V: { min: 0, max: 0 } // Vacant: $0 only
                            }
                        },
                        coverageD: {
                            byResidenceUsage: {
                                P: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                                S: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                                R: { min: 3000, max: 50000, allowEditing: true },
                                V: { min: 0, max: 0, allowEditing: false }
                            }
                        }
                    }
                },
                [FarmRiskType.Fo4]: {
                    hiddenCoverages: [
                        'deductibleEarthquake',
                        'earthquakeCoverage',
                        'expandedDebrisRemoval',
                        'mineSubsidenceCoverage',
                        'inlandFloodCoverage'
                    ],
                    inlandMarine: {
                        itemTypes: dwellingInlandMarineItemTypes,
                        inlandMarineDeductible: allOtherOptions.filter((option) => [250, 500, 1000, 2500, 5000].includes(option.id))
                    },
                    customFormComponentPaths: {
                        deductibleAllOther: 'base/deductible-all-other',
                        deductibleWindHail: 'base/deductible-wind-hail',
                        replCostMobileHome: 'base/repl-cost-mobile-home'
                    }
                },
                [FarmRiskType.Fo6Scheduled]: {
                    hiddenCoverages: [
                        'earthquakeCoverage',
                        'deductibleEarthquake',
                        'expandedDebrisRemoval',
                        'hasSnowWeightCoverage'
                    ],
                    coverageF: {
                        itemTypes: scheduledFarmPropertyCategories.filter((option) => !['10', '11', '12', '13', '14'].includes(option.id)),
                        deductibleOptions: [
                            { id: 1500, value: '$1,500' },
                            { id: 2500, value: '$2,500' },
                            { id: 5000, value: '$5,000' },
                            { id: 10000, value: '$10,000' },
                            { id: 20000, value: '$20,000' }
                        ],
                        livestockDeductibleOptions: [
                            { id: 500, value: '$500' },
                            { id: 1500, value: '$1,500' },
                            { id: 2500, value: '$2,500' },
                            { id: 5000, value: '$5,000' },
                            { id: 10000, value: '$10,000' },
                            { id: 20000, value: '$20,000' }
                        ],
                        defaultDeductible: 1500,
                        showWindHailDeductible: true,
                        itemOverrides: {
                            '1': {
                                descriptionLabel: 'Description (Make/Model)',
                                showYear: true,
                                serialNumberRequired: false,
                                hideReplacementCost: true,
                                openPerilsAlwaysOn: true,
                                hideMakeField: true
                            },
                            '2': {
                                descriptionLabel: 'Description (Make/Model)',
                                showYear: true,
                                serialNumberRequired: false,
                                openPerilsAlwaysOn: true,
                                hideMakeField: true
                            },
                            '3': {
                                descriptionLabel: 'Description (Make/Model)',
                                showYear: true,
                                serialNumberRequired: false,
                                hideReplacementCost: true,
                                openPerilsAlwaysOn: true,
                                hideMakeField: true
                            },
                            '4': { descriptionLabel: 'Description' },
                            '5': { descriptionLabel: 'Description' },
                            '6': {
                                descriptionLabel: 'Description (Make/Model)',
                                hideReplacementCost: true,
                                hideOpenPerils: true,
                                hideMakeField: true
                            },
                            '7': {
                                descriptionLabel: 'Description',
                                winterPerilsAlwaysOn: true,
                                hideSpecialCattleEndorsement: true,
                                hideSuffocationOfLivestock: true
                            },
                            '8': { descriptionLabel: 'Description (Make/Model)', hideMakeField: true },
                            '9': { descriptionLabel: 'Description (Make/Model)', hideMakeField: true }
                        }
                    },
                    peakSeason: {
                        itemTypes: scheduledFarmPropertyCategories.filter((option) => ['4', '5', '14'].includes(option.id))
                    },
                    availableScheduledCoverageTypes: [
                        FarmScheduleCoverageType.SPP,
                        FarmScheduleCoverageType.PEAK,
                        FarmScheduleCoverageType.INCOME
                    ],
                    inlandMarine: {
                        itemTypes: dwellingInlandMarineItemTypes,
                        inlandMarineDeductible: allOtherOptions.filter((option) => [250, 500, 1000, 2500, 5000].includes(option.id))
                    },
                    customFormComponentPaths: {}
                },
                [FarmRiskType.Fo6Unscheduled]: {
                    hiddenCoverages: ['earthquakeCoverage', 'deductibleEarthquake'],
                    availableScheduledCoverageTypes: [FarmScheduleCoverageType.SPP],
                    inlandMarine: {
                        itemTypes: dwellingInlandMarineItemTypes,
                        inlandMarineDeductible: allOtherOptions.filter((option) => [250, 500, 1000, 2500, 5000].includes(option.id))
                    },
                    customFormComponentPaths: {}
                },
                [FarmRiskType.Fo6Structures]: {
                    hiddenCoverages: ['earthquakeCoverage', 'deductibleEarthquake', 'hasEarthquakeExteriorVeneerCoverage'],
                    permissionRestrictedOptions: {
                        roofLossSettlement: {
                            permission: 'canApproveUWReview',
                            hiddenOptionsWithoutPermission: ['RC']
                        }
                    },
                    excludedConstructionTypes: ['MH'],
                    /**
                     * Business rules for Forreston FO6 Structures:
                     * - Building class 1 max age: 25 years
                     * - Confinement building RC eligibility max age: 15 years
                     * - Coverage E: RC buildings 80-100% of RCE; ACV buildings 80% of ACV minimum, up to 100% RCE max
                     *
                     * Related issues: #28684, #28945, #28950, #28923
                     */
                    businessRules: {
                        buildingClass1MaxAgeYears: 25,
                        confinementBuildingMaxAgeYears: 15,
                        poultryBuildingMaxAgeYears: 15,
                        coverageE: {
                            rceBasedStructures: {
                                minPercent: 0.8, // 80% of RCE minimum
                                maxPercent: 1.0 // 100% of RCE maximum
                            },
                            acvBasedStructures: {
                                minPercent: 0.8 // 80% of ACV minimum; max is 100% RCE
                            }
                        }
                    },
                    availableScheduledCoverageTypes: [
                        FarmScheduleCoverageType.SPP,
                        FarmScheduleCoverageType.INCOME
                        // Note: RELATED_PRIVATE_STRUCTURES intentionally excluded
                    ],
                    inlandMarine: {
                        itemTypes: dwellingInlandMarineItemTypes,
                        inlandMarineDeductible: allOtherOptions.filter((option) => [250, 500, 1000, 2500, 5000].includes(option.id))
                    },
                    customFormComponentPaths: {
                        coverageMS: 'base/coverage-ms',
                        deductibleAllOther: 'base/deductible-all-other',
                        deductibleWindHail: 'base/deductible-wind-hail'
                    }
                }
            }
        },
        supportedFees: {
            creditCard: true
        }
    }
};
export const constants = brand.constants;
export const features = brand.features;
export const theme = brand.theme;
export default brand.config;
